<script setup lang="ts">
import { toast } from 'vue-sonner';
import { Pencil, Plus, Trash2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { contractFieldKey, contractFieldKeys, contractTableKey, contractTableKeys, removeContractTableTokens, updateContractTableLabel } from '~/lib/contractDocument';
import { hasActiveProposalPublicLink } from '~/lib/proposalPublicLink';
import { useProposalsStore } from '~/stores/proposals';
import type { ContractFieldDefinition, ContractFieldType, ContractTableDefinition, ContractTableColumn, Proposal } from '~/types/models';

const FIELD_TYPES: Array<{ value: ContractFieldType; label: string }> = [
    { value: 'text', label: 'Text' },
    { value: 'date', label: 'Date' },
    { value: 'proposal', label: 'Proposal' },
];

const props = withDefaults(defineProps<{
    fields: ContractFieldDefinition[];
    tables?: ContractTableDefinition[];
    values?: Record<string, string>;
    proposals?: Proposal[];
    proposalsLoading?: boolean;
    proposalReferences?: Record<string, string>;
    proposalReferenceLinks?: Record<string, string>;
    contentHtml?: string;
    editing?: boolean;
    showValues?: boolean;
    title?: string;
    emptyText?: string;
    fillOnly?: boolean;
}>(), { tables: () => [], values: () => ({}), proposals: () => [], proposalsLoading: false, proposalReferences: () => ({}), proposalReferenceLinks: () => ({}), contentHtml: '', editing: true, showValues: true, title: 'Variables', emptyText: 'No variables yet.', fillOnly: false });

const emit = defineEmits<{
    'update:fields': [value: ContractFieldDefinition[]];
    'update:tables': [value: ContractTableDefinition[]];
    'update:contentHtml': [value: string];
    'update:values': [value: Record<string, string>];
    'update:proposalReferences': [value: Record<string, string>];
    'update:proposalReferenceLinks': [value: Record<string, string>];
    insertField: [key: string, label: string];
    insertTable: [key: string, label: string];
    insertSignature: [role: 'tenant' | 'client'];
}>();

const newFieldLabel = ref('');
const newFieldType = ref<ContractFieldType>('text');
const proposalStore = useProposalsStore();
const usedFieldKeys = computed(() => contractFieldKeys(props.contentHtml));
const usedTableKeys = computed(() => contractTableKeys(props.contentHtml));
const defaultDialogOpen = ref(false);
const defaultFieldKey = ref('');
const defaultDraft = ref('');
const tableDialogOpen = ref(false);
const tableDraftLabel = ref('');
const tableDraftColumns = ref('');
const editingTableKey = ref('');

function addField() {
    const label = newFieldLabel.value.trim();
    const key = contractFieldKey(label);
    if (!key) return toast.error('Give the variable a name using letters or numbers.');
    if (props.fields.some(field => field.key === key)) return toast.error('A variable with that name already exists.');
    emit('update:fields', [...props.fields, { key, label, required: true, type: newFieldType.value }]);
    emit('insertField', key, label);
    newFieldLabel.value = '';
    newFieldType.value = 'text';
}

function setValue(key: string, value: string) {
    emit('update:values', { ...props.values, [key]: value });
}

function openDefaultEditor(field: ContractFieldDefinition) {
    defaultFieldKey.value = field.key;
    defaultDraft.value = field.defaultValue || '';
    defaultDialogOpen.value = true;
}

function saveDefaultValue() {
    emit('update:fields', props.fields.map(field => field.key === defaultFieldKey.value ? { ...field, defaultValue: defaultDraft.value } : field));
    defaultDialogOpen.value = false;
}

function setRequired(key: string, required: boolean) {
    emit('update:fields', props.fields.map(field => field.key === key ? { ...field, required } : field));
}

function setFieldType(key: string, type: ContractFieldType) {
    const currentField = props.fields.find(field => field.key === key);
    if (!currentField || fieldType(currentField) === type) return;

    emit('update:fields', props.fields.map(field => {
        if (field.key !== key) return field;
        const nextField = { ...field, type };
        delete nextField.defaultValue;
        return nextField;
    }));
    if (props.showValues) setValue(key, '');
    if (props.proposalReferences[key]) {
        const nextReferences = { ...props.proposalReferences };
        delete nextReferences[key];
        emit('update:proposalReferences', nextReferences);
    }
    if (props.proposalReferenceLinks[key]) {
        const nextLinks = { ...props.proposalReferenceLinks };
        delete nextLinks[key];
        emit('update:proposalReferenceLinks', nextLinks);
    }
}

function setProposalValue(key: string, proposalId: string) {
    const proposal = props.proposals.find(item => item.id === proposalId);
    setValue(key, proposal?.title || '');
    const nextReferences = { ...props.proposalReferences };
    const nextLinks = { ...props.proposalReferenceLinks };
    if (proposal) nextReferences[key] = proposal.id;
    else delete nextReferences[key];
    if (proposal && hasActiveProposalPublicLink(proposal)) {
        try {
            const publicLink = proposalStore.buildPublicLink(proposal);
            if (publicLink) nextLinks[key] = publicLink;
            else delete nextLinks[key];
        } catch {
            delete nextLinks[key];
        }
    } else delete nextLinks[key];
    emit('update:proposalReferences', nextReferences);
    emit('update:proposalReferenceLinks', nextLinks);
}

function proposalLinkForField(key: string): string | null {
    const proposal = props.proposals.find(item => item.id === props.proposalReferences[key]);
    if (proposal) {
        if (!hasActiveProposalPublicLink(proposal)) return null;
        try { return proposalStore.buildPublicLink(proposal); }
        catch { return null; }
    }
    const storedLink = props.proposalReferenceLinks[key];
    if (storedLink) return storedLink;
    return null;
}

function proposalLinkExpiredForField(key: string): boolean {
    const proposal = props.proposals.find(item => item.id === props.proposalReferences[key]);
    return Boolean(proposal?.publicAccessToken && proposal.publicSharedAt && !hasActiveProposalPublicLink(proposal));
}

function fieldType(field: ContractFieldDefinition): ContractFieldType {
    return field.type || 'text';
}

function defaultFieldInputType(): 'date' | 'text' {
    const field = props.fields.find(item => item.key === defaultFieldKey.value);
    return field && fieldType(field) === 'date' ? 'date' : 'text';
}

function openTableEditor(table?: ContractTableDefinition) {
    editingTableKey.value = table?.key || '';
    tableDraftLabel.value = table?.label || '';
    tableDraftColumns.value = table?.columns.map(column => column.label).join(', ') || '';
    tableDialogOpen.value = true;
}

function columnsFromLabels(value: string): ContractTableColumn[] {
    const labels = value.split(/[,\n]/).map(label => label.trim()).filter(Boolean);
    const columns: ContractTableColumn[] = [];
    for (const [index, label] of labels.entries()) {
        const baseKey = contractFieldKey(label) || `column_${index + 1}`;
        let key = baseKey;
        let suffix = 2;
        while (columns.some(column => column.key === key)) key = `${baseKey}_${suffix++}`;
        columns.push({ key, label });
    }
    return columns;
}

function saveTableDefinition() {
    const label = tableDraftLabel.value.trim();
    const columns = columnsFromLabels(tableDraftColumns.value);
    if (!label) return toast.error('Give the table a name.');
    if (!columns.length) return toast.error('Add at least one column.');
    const current = props.tables.find(table => table.key === editingTableKey.value);
    const key = current?.key || contractTableKey(label);
    if (!key) return toast.error('Give the table a name using letters or numbers.');
    if (!current && props.tables.some(table => table.key === key)) return toast.error('A table with that name already exists.');
    const nextTable = { key, label, columns };
    emit('update:tables', current
        ? props.tables.map(table => table.key === key ? nextTable : table)
        : [...props.tables, nextTable]);
    if (current && current.label !== label) emit('update:contentHtml', updateContractTableLabel(props.contentHtml, key, label));
    if (!current) emit('insertTable', key, label);
    tableDialogOpen.value = false;
}

function removeTable(key: string) {
    emit('update:tables', props.tables.filter(table => table.key !== key));
    emit('update:contentHtml', removeContractTableTokens(props.contentHtml, key));
}
</script>

<template>
    <div class="space-y-5">
        <section class="space-y-3">
            <div><h3 class="text-sm font-semibold">{{ title }}</h3><p class="mt-1 text-xs text-muted-foreground">{{ fillOnly ? 'These values are used wherever the variables appear in this contract.' : 'Insert the same variable anywhere it is needed. Fill it once for each contract.' }}</p></div>
            <div v-if="editing && !fillOnly" class="flex flex-wrap gap-2"><Input v-model="newFieldLabel" class="min-w-36 flex-1" aria-label="New variable name" placeholder="e.g. Project fee" @keydown.enter.prevent="addField" /><Select v-model="newFieldType"><SelectTrigger class="w-32" aria-label="New variable type"><SelectValue /></SelectTrigger><SelectContent><SelectItem v-for="type in FIELD_TYPES" :key="type.value" :value="type.value">{{ type.label }}</SelectItem></SelectContent></Select><Button type="button" variant="outline" size="sm" @click="addField">Add</Button></div>
            <div v-if="fields.length" class="divide-y border-y border-border/60">
                <div v-for="field in fields" :key="field.key" class="group space-y-2 py-3">
                    <div class="flex items-center justify-between gap-2"><div class="min-w-0"><Label v-if="showValues && fieldType(field) !== 'proposal'" :for="`contract-field-${field.key}`" class="font-medium">{{ field.label }}</Label><p v-else class="text-sm font-medium">{{ field.label }}</p><p v-if="!usedFieldKeys.includes(field.key)" class="text-xs text-muted-foreground">Not placed yet</p></div><div class="flex shrink-0 items-center gap-1"><Select v-if="editing && !fillOnly" :model-value="fieldType(field)" @update:model-value="setFieldType(field.key, String($event) as ContractFieldType)"><SelectTrigger class="h-8 w-28 text-xs" :aria-label="`Type for ${field.label}`"><SelectValue /></SelectTrigger><SelectContent><SelectItem v-for="type in FIELD_TYPES" :key="type.value" :value="type.value">{{ type.label }}</SelectItem></SelectContent></Select><Button v-if="editing && !fillOnly" type="button" variant="ghost" size="sm" class="h-7 shrink-0 px-1 text-xs font-normal text-muted-foreground/65 opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 group-focus-within:opacity-100" :aria-label="`Insert ${field.label} into document`" @click="emit('insertField', field.key, field.label)">Insert</Button></div></div>
                    <Input v-if="showValues && fieldType(field) !== 'proposal'" :id="`contract-field-${field.key}`" :type="fieldType(field) === 'date' ? 'date' : 'text'" :model-value="values[field.key] ?? field.defaultValue ?? ''" :disabled="!editing" :placeholder="fieldType(field) === 'date' ? undefined : `Enter ${field.label.toLowerCase()}`" @update:model-value="setValue(field.key, String($event))" />
                    <template v-else-if="showValues && fieldType(field) === 'proposal'"><SearchableSelectMenu :model-value="proposalReferences[field.key] || ''" :options="proposals.map(proposal => ({ value: proposal.id, label: proposal.title, description: proposal.status }))" :placeholder="proposalsLoading ? 'Loading client proposals...' : proposals.length ? 'Select a client proposal' : 'No proposals linked to this client'" search-placeholder="Search proposals..." allow-clear clear-label="No linked proposal" show-all-by-default variant="form" :disabled="!editing || proposalsLoading" @update:model-value="setProposalValue(field.key, $event)" /><p v-if="values[field.key]" class="text-xs text-muted-foreground">Contract text: {{ values[field.key] }}</p><p v-if="proposalReferences[field.key] && proposalLinkForField(field.key)" class="text-xs text-muted-foreground">Clients can open this proposal from the contract.</p><p v-else-if="proposalReferences[field.key] && proposalLinkExpiredForField(field.key)" class="text-xs text-muted-foreground">This proposal link has expired. Copy a new public link from the proposal to refresh it.</p><p v-else-if="proposalReferences[field.key]" class="text-xs text-muted-foreground">Share this proposal before sending the contract so clients can open it.</p></template>
                    <div v-else class="flex min-h-8 items-center justify-between gap-2">
                        <p class="min-w-0 truncate text-sm text-muted-foreground">{{ fieldType(field) === 'proposal' ? 'Select a proposal when creating the contract' : field.defaultValue || 'No default value' }}</p>
                        <Button v-if="editing && fieldType(field) !== 'proposal'" type="button" variant="ghost" size="icon" class="size-7 shrink-0 text-muted-foreground" :aria-label="`Edit default value for ${field.label}`" @click="openDefaultEditor(field)"><Pencil class="size-3.5" /></Button>
                    </div>
                    <label v-if="editing && !fillOnly" class="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" :checked="field.required" @change="setRequired(field.key, ($event.target as HTMLInputElement).checked)" />Required before sending</label>
                    <p v-else-if="fillOnly && field.required && usedFieldKeys.includes(field.key)" class="text-xs text-muted-foreground">Required before sending</p>
                </div>
            </div>
            <p v-else class="text-sm text-muted-foreground">{{ emptyText }}</p>
        </section>

        <section v-if="editing && !fillOnly" class="space-y-3 border-t border-border/70 pt-5">
            <div class="flex items-center justify-between gap-3"><div><h3 class="text-sm font-semibold">Dynamic tables</h3><p class="mt-1 text-xs text-muted-foreground">Add a table whose rows can change for each contract.</p></div><Button type="button" variant="outline" size="sm" @click="openTableEditor()"><Plus class="mr-1 size-3.5" />Add table</Button></div>
            <div v-if="tables.length" class="divide-y border-y border-border/60">
                <div v-for="table in tables" :key="table.key" class="flex items-center justify-between gap-2 py-3">
                    <div class="min-w-0"><p class="truncate text-sm font-medium">{{ table.label }}</p><p class="text-xs text-muted-foreground">{{ table.columns.map(column => column.label).join(' · ') }}<span v-if="!usedTableKeys.includes(table.key)"> · Not placed yet</span></p></div>
                    <div class="flex shrink-0 items-center gap-1"><Button type="button" variant="ghost" size="sm" class="h-7 px-2 text-xs" @click="emit('insertTable', table.key, table.label)">Insert</Button><Button type="button" variant="ghost" size="icon" class="size-7 text-muted-foreground" :aria-label="`Edit ${table.label}`" @click="openTableEditor(table)"><Pencil class="size-3.5" /></Button><Button type="button" variant="ghost" size="icon" class="size-7 text-muted-foreground" :aria-label="`Remove ${table.label}`" @click="removeTable(table.key)"><Trash2 class="size-3.5" /></Button></div>
                </div>
            </div>
        </section>

        <section v-if="editing && !fillOnly" class="space-y-3 border-t border-border/70 pt-5"><div><h3 class="text-sm font-semibold">Signature positions</h3><p class="mt-1 text-xs text-muted-foreground">Place each signature where it belongs in the agreement.</p></div><div class="flex flex-wrap gap-2"><Button type="button" variant="outline" size="sm" @click="emit('insertSignature', 'tenant')">Tenant signature</Button><Button type="button" variant="outline" size="sm" @click="emit('insertSignature', 'client')">Client signature</Button></div></section>

        <Dialog :open="defaultDialogOpen" @update:open="defaultDialogOpen = $event">
            <DialogContent class="sm:max-w-md">
                <DialogHeader><DialogTitle>Edit default value</DialogTitle><DialogDescription>{{ fields.find(field => field.key === defaultFieldKey)?.label || 'Variable' }}. This value is suggested when a new contract uses this template.</DialogDescription></DialogHeader>
                <div class="space-y-2"><Label for="contract-variable-default">Default value</Label><Input id="contract-variable-default" v-model="defaultDraft" :type="defaultFieldInputType()" autofocus /></div>
                <DialogFooter><Button type="button" variant="outline" @click="defaultDialogOpen = false">Cancel</Button><Button type="button" @click="saveDefaultValue">Save value</Button></DialogFooter>
            </DialogContent>
        </Dialog>

        <Dialog :open="tableDialogOpen" @update:open="tableDialogOpen = $event">
            <DialogContent class="sm:max-w-md">
                <DialogHeader><DialogTitle>{{ editingTableKey ? 'Edit dynamic table' : 'Add dynamic table' }}</DialogTitle><DialogDescription>Choose the columns once. Each contract can have its own rows.</DialogDescription></DialogHeader>
                <div class="space-y-4"><div class="space-y-2"><Label for="contract-table-label">Table name</Label><Input id="contract-table-label" v-model="tableDraftLabel" placeholder="e.g. Payment schedule" /></div><div class="space-y-2"><Label for="contract-table-columns">Columns</Label><Input id="contract-table-columns" v-model="tableDraftColumns" placeholder="Milestone, Due date, Amount" /><p class="text-xs text-muted-foreground">Separate column names with commas.</p></div></div>
                <DialogFooter><Button type="button" variant="outline" @click="tableDialogOpen = false">Cancel</Button><Button type="button" @click="saveTableDefinition">{{ editingTableKey ? 'Save table' : 'Add table' }}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>
